"use client";

import { useState } from "react";
import {
  SourceExtractionWorkflow,
  useBacklogSearchSync,
} from "@/features/backlog";
import {
  OriginEditForm,
  OriginItemDetail,
  OriginSubmitForm,
  type EditableOriginInput,
} from "@/features/origins";
import { PROFILE_SHAPING_LABELS } from "@/features/profile-shaping";
import {
  SuggestedThoughtConfirmationDialog,
  SuggestedThoughtConfirmationProvider,
} from "@/features/thoughts";
import { turnOriginsToMeiliOrigins } from "@/core/search";
import type { MeiliOrigin, Origin } from "@/generated/fastapi";
import { useInterimStateManager } from "@/state/globalStore";
import WithDrawerOpen from "@/shared/ui/gestures/WithDrawerOpen";

function getEditableOriginInput(origin: MeiliOrigin): EditableOriginInput {
  return {
    id: origin.objectID,
    raw_text_input: origin.raw_text_input,
    user_note: origin.user_note,
    language: origin.language,
    latest_at: origin.latest_at,
  };
}

export default function PrivateSourceDrawers() {
  const [originForExtraction, setOriginForExtraction] =
    useState<MeiliOrigin | null>(null);
  const { interimState, setInterimState, resetInterimState } =
    useInterimStateManager();
  const { syncBacklogOriginCreated, syncBacklogOriginUpdated } =
    useBacklogSearchSync();
  const originDetailValue = interimState.origin_detail?.value;
  const editOriginValue = interimState.edit_origin?.value;
  const extendOriginValue = interimState.extend_origin?.value;
  const resetAddOriginDrawer = () => {
    setOriginForExtraction(null);
    resetInterimState("add_origin");
  };
  const syncUpdatedOriginDrawers = (updatedOrigin: MeiliOrigin) => {
    if (
      interimState.origin_detail?.isOpen &&
      originDetailValue?.origin.objectID === updatedOrigin.objectID
    ) {
      setInterimState("origin_detail", {
        isOpen: true,
        value: {
          ...originDetailValue,
          origin: updatedOrigin,
        },
      });
    }

    if (
      interimState.extend_origin?.isOpen &&
      extendOriginValue?.origin.objectID === updatedOrigin.objectID
    ) {
      setInterimState("extend_origin", {
        isOpen: true,
        value: {
          ...extendOriginValue,
          origin: updatedOrigin,
        },
      });
    }
  };
  const syncUpdatedOrigin = (updatedOrigin: Origin) => {
    const updatedMeiliOrigin = turnOriginsToMeiliOrigins([updatedOrigin])[0];

    syncBacklogOriginUpdated();
    syncUpdatedOriginDrawers(updatedMeiliOrigin);
  };

  return (
    <>
      <WithDrawerOpen
        title={
          originForExtraction
            ? PROFILE_SHAPING_LABELS.flow
            : PROFILE_SHAPING_LABELS.addSourceMaterial
        }
        isOpen={interimState.add_origin?.isOpen ?? false}
        onOpenChangeAction={(isOpen) => {
          if (!isOpen) {
            resetAddOriginDrawer();
            return;
          }

          setInterimState("add_origin", { isOpen });
        }}
        onCloseAction={resetAddOriginDrawer}
      >
        {originForExtraction ? (
          <SuggestedThoughtConfirmationProvider>
            <SourceExtractionWorkflow
              meiliOrigin={originForExtraction}
              onUpdateSearchResultAction={() => {
                syncBacklogOriginUpdated();
              }}
            />
            <SuggestedThoughtConfirmationDialog />
          </SuggestedThoughtConfirmationProvider>
        ) : (
          <OriginSubmitForm
            sourcedWhere="backlog"
            submitAndContinueButtonText={
              PROFILE_SHAPING_LABELS.originCreationAndExtractionAction
            }
            onSuccess={(origin, intent) => {
              syncBacklogOriginCreated();

              if (intent === "add_and_extract") {
                setOriginForExtraction(turnOriginsToMeiliOrigins([origin])[0]);
              }
            }}
          />
        )}
      </WithDrawerOpen>

      <WithDrawerOpen
        title="Source material"
        isOpen={interimState.origin_detail?.isOpen ?? false}
        onOpenChangeAction={(isOpen) => {
          setInterimState("origin_detail", {
            isOpen,
            value: interimState.origin_detail?.value,
          });
        }}
        onCloseAction={() => {
          resetInterimState("origin_detail");
        }}
      >
        {originDetailValue ? (
          <OriginItemDetail
            key={`preview-${originDetailValue.origin.objectID}`}
            origin={originDetailValue.origin}
            onExtendSuccess={syncUpdatedOrigin}
            searchQuery={originDetailValue.searchQuery}
          />
        ) : null}
      </WithDrawerOpen>

      <WithDrawerOpen
        title="Edit source material"
        isOpen={interimState.edit_origin?.isOpen ?? false}
        onOpenChangeAction={(isOpen) => {
          setInterimState("edit_origin", {
            isOpen,
            value: interimState.edit_origin?.value,
          });
        }}
        onCloseAction={() => {
          resetInterimState("edit_origin");
        }}
      >
        {editOriginValue ? (
          <OriginEditForm
            originToEdit={getEditableOriginInput(editOriginValue.origin)}
            onSuccess={(updatedOrigin) => {
              syncUpdatedOrigin(updatedOrigin);
              resetInterimState("edit_origin");
            }}
          />
        ) : null}
      </WithDrawerOpen>

      <WithDrawerOpen
        title="Source material"
        isOpen={interimState.extend_origin?.isOpen ?? false}
        onOpenChangeAction={(isOpen) => {
          setInterimState("extend_origin", {
            isOpen,
            value: interimState.extend_origin?.value,
          });
        }}
        onCloseAction={() => {
          resetInterimState("extend_origin");
        }}
      >
        {extendOriginValue ? (
          <OriginItemDetail
            key={`extend-${extendOriginValue.origin.objectID}`}
            initialMode="extend"
            origin={extendOriginValue.origin}
            onCancelExtend={() => {
              resetInterimState("extend_origin");
            }}
            onExtendSuccess={syncUpdatedOrigin}
          />
        ) : null}
      </WithDrawerOpen>
    </>
  );
}
